import { supabase } from "../lib/supabase";

/**
 * Generate a file path for customer packing slip with company-based structure
 */
const generateCompanyBasedPath = (
  companyId: number | string,
  bookingId: number | string,
  fileName: string
): string => {
  // Sanitize the filename to remove special characters and spaces
  const sanitizedFileName = fileName
    .replace(/\s+/g, "_") // Replace spaces with underscores
    .replace(/[^\w.-]/g, "") // Remove any non-alphanumeric characters except dots and hyphens
    .normalize("NFD") // Normalize accented characters
    .replace(/[\u0300-\u036f]/g, ""); // Remove diacritics

  // Create a clean path structure with company_id as root
  return `${companyId}/${bookingId}/${Date.now()}_${sanitizedFileName}`;
};

/**
 * Upload a customer packing slip file using the company-based path structure
 */
export const uploadCustomerPackingSlip = async (
  bookingId: number | string,
  file: File,
  companyId: number | string = 0
): Promise<{ error: unknown; path?: string } | null> => {
  try {
    if (!file) {
      console.error("No file provided for upload");
      return { error: "No file provided" };
    }
    
    console.log("uploadCustomerPackingSlip starting with:", {
      bookingId,
      companyId,
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type
    });
    
    // Get company ID from booking if not provided
    const finalCompanyId = companyId || 0; // Use 0 as default company ID if not provided
    
    // Generate path with company ID as root
    const filePath = generateCompanyBasedPath(finalCompanyId, bookingId, file.name);
    
    console.log("Generated file path:", filePath);
    
    // Upload to Supabase storage
    console.log("Uploading to bucket:", "customerpackingslips");
    const { data, error } = await supabase.storage
      .from("customerpackingslips")
      .upload(filePath, file, {
        cacheControl: "3600",
        upsert: false,
      });
    
    if (error) {
      console.error("Error uploading customer packing slip:", error);
      return { error };
    }
    
    console.log("Customer packing slip uploaded successfully:", data.path);
    return { error: null, path: data.path };
  } catch (error) {
    console.error("Error in uploadCustomerPackingSlip:", error);
    return { error };
  }
};

/**
 * Download a customer packing slip file
 */
export const downloadCustomerPackingSlip = async (
  filePath: string,
  fileName?: string
): Promise<void> => {
  try {
    console.log("Downloading customer packing slip:", filePath);
    
    // Create a signed URL for download
    const { data, error } = await supabase.storage
      .from("customerpackingslips")
      .createSignedUrl(filePath, 60); // URL expires in 60 seconds

    if (error) {
      console.error("Error creating signed URL:", error);
      throw error;
    }

    if (!data?.signedUrl) {
      throw new Error("No signed URL returned");
    }

    // Create a temporary anchor element to trigger download
    const link = document.createElement("a");
    link.href = data.signedUrl;
    link.download = fileName || filePath.split("/").pop() || "customer_packing_slip.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    console.log("Customer packing slip download initiated");
  } catch (error) {
    console.error("Error downloading customer packing slip:", error);
    throw error;
  }
};

/**
 * Create a signed URL for customer packing slip preview/download
 */
export const createCustomerPackingSlipSignedUrl = async (
  filePath: string,
  expiresIn: number = 3600,
  logError: boolean = false
): Promise<string | null> => {
  try {
    console.log("Creating signed URL for customer packing slip:", filePath);
    
    const { data, error } = await supabase.storage
      .from("customerpackingslips")
      .createSignedUrl(filePath, expiresIn);

    if (error) {
      if (logError) {
        console.error("Error creating customer packing slip signed URL:", error);
      }
      return null;
    }

    return data?.signedUrl || null;
  } catch (error) {
    if (logError) {
      console.error("Error in createCustomerPackingSlipSignedUrl:", error);
    }
    return null;
  }
};

/**
 * Get public URL for customer packing slip (if bucket is public)
 */
export const getCustomerPackingSlipPublicUrl = (
  filePath: string,
  logError: boolean = false
): string | null => {
  try {
    const { data } = supabase.storage
      .from("customerpackingslips")
      .getPublicUrl(filePath);

    return data?.publicUrl || null;
  } catch (error) {
    if (logError) {
      console.error("Error getting customer packing slip public URL:", error);
    }
    return null;
  }
}; 